import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import "./SalesPages.css";

function DemoAccountProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [demoAccount, setDemoAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState(null);

  const rootDomain = "agentbookcrm.com";

  useEffect(() => {
    loadDemoAccount();
  }, [id]);

  const loadDemoAccount = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/demo-accounts/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error("Failed to load demo account");
      }

      const data = await res.json();
      setDemoAccount(data);
      setSubdomainStatus(null);
    } catch (err) {
      console.error("Load demo account error:", err.message);
      setDemoAccount(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setDemoAccount((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const checkSubdomain = async (value = demoAccount?.subdomain || "") => {
    try {
      const trimmed = String(value || "").trim();

      if (!trimmed) {
        setSubdomainStatus(null);
        return null;
      }

      setCheckingSubdomain(true);

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE_URL}/api/demo-accounts/check-subdomain?value=${encodeURIComponent(trimmed)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to check subdomain");
      }

      const currentSubdomain = String(demoAccount?.subdomain || "").trim().toLowerCase();
      const normalizedReturned = String(data.normalized || "").trim().toLowerCase();

      if (currentSubdomain && normalizedReturned && currentSubdomain === normalizedReturned) {
        const ownValueResult = { ...data, available: true };
        setSubdomainStatus(ownValueResult);
        return ownValueResult;
      }

      setSubdomainStatus(data);
      return data;
    } catch (err) {
      console.error("Check demo subdomain error:", err.message);
      const failed = {
        available: false,
        reason: err.message || "Failed to check subdomain"
      };
      setSubdomainStatus(failed);
      return failed;
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (!demoAccount?.demo_name || !demoAccount.demo_name.trim()) {
        throw new Error("Demo name is required");
      }

      if (!demoAccount?.subdomain || !demoAccount.subdomain.trim()) {
        throw new Error("Subdomain is required");
      }

      const subdomainResult = await checkSubdomain(demoAccount.subdomain);

      if (!subdomainResult?.available) {
        throw new Error(subdomainResult?.reason || "That subdomain is not available");
      }

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/demo-accounts/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          demo_name: demoAccount.demo_name,
          subdomain: demoAccount.subdomain,
          status: demoAccount.status || "active",
          expires_at: demoAccount.expires_at || null,
          notes: demoAccount.notes || null
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to save demo account");
      }

      alert("Demo account saved successfully.");
      loadDemoAccount();
    } catch (err) {
      console.error("Save demo account error:", err.message);
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDemo = async () => {
    try {
      setLaunching(true);

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/demo-accounts/${id}/launch`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to launch demo account");
      }

      if (!data.launch_url) {
        throw new Error("Launch URL was not returned.");
      }

      window.open(data.launch_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Launch demo account error:", err.message);
      alert(err.message);
    } finally {
      setLaunching(false);
    }
  };

  const statusClass = (value) =>
    `sales-chip ${String(value || "").toLowerCase().replace(/\s+/g, "-")}`;

  if (loading) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Loading demo account...</div>
        </div>
      </>
    );
  }

  if (!demoAccount) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Demo account not found.</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">{demoAccount.demo_name || "Demo Account"}</h1>
            <div className="sales-hero-meta">
              <span className={statusClass(demoAccount.status || "active")}>
                {demoAccount.status || "active"}
              </span>
              <span className="sales-chip sales-chip-neutral">Demo Account</span>
              {demoAccount.created_by_name && (
                <span className="sales-chip sales-chip-outline">{demoAccount.created_by_name}</span>
              )}
            </div>
          </div>

          <div className="sales-header-actions">
            <button className="sales-secondary-btn" onClick={() => navigate("/demo-accounts")}>
              Back to Demo Accounts
            </button>
            <button className="sales-secondary-btn" onClick={handleOpenDemo} disabled={launching}>
              {launching ? "Opening..." : "Open Demo"}
            </button>
            <button className="sales-primary-btn" onClick={handleSave} disabled={saving || checkingSubdomain}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="sales-profile-grid">
          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Demo Information</h2>
              <p>Manage the demo environment details and subdomain configuration.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field sales-field-full">
                <label>Demo Name</label>
                <input
                  value={demoAccount.demo_name || ""}
                  onChange={(e) => handleChange("demo_name", e.target.value)}
                />
              </div>

              <div className="sales-field">
                <label>Status</label>
                <select
                  value={demoAccount.status || "active"}
                  onChange={(e) => handleChange("status", e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="sales-field">
                <label>Expires At</label>
                <input
                  type="date"
                  value={demoAccount.expires_at ? String(demoAccount.expires_at).slice(0, 10) : ""}
                  onChange={(e) => handleChange("expires_at", e.target.value)}
                />
              </div>

              <div className="sales-field">
                <label>Subdomain</label>
                <input
                  value={demoAccount.subdomain || ""}
                  onChange={(e) => {
                    handleChange("subdomain", e.target.value.toLowerCase());
                    setSubdomainStatus(null);
                  }}
                  onBlur={() => checkSubdomain()}
                  placeholder="florida-aca-demo"
                />
                {checkingSubdomain && <small>Checking subdomain...</small>}
                {!checkingSubdomain && subdomainStatus?.available && (
                  <small style={{ color: "#15803d" }}>Subdomain is available.</small>
                )}
                {!checkingSubdomain && subdomainStatus && !subdomainStatus.available && (
                  <small style={{ color: "#b91c1c" }}>{subdomainStatus.reason || "Subdomain is unavailable."}</small>
                )}
              </div>

              <div className="sales-field">
                <label>Full Domain</label>
                <input
                  value={
                    demoAccount.full_domain ||
                    (demoAccount.subdomain ? `${String(demoAccount.subdomain).trim().toLowerCase()}.${rootDomain}` : "")
                  }
                  readOnly
                />
              </div>

              <div className="sales-field sales-field-full">
                <label>Notes</label>
                <textarea
                  value={demoAccount.notes || ""}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  rows="5"
                  style={{ width: "100%", resize: "vertical" }}
                />
              </div>
            </div>
          </div>

          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Demo Launch</h2>
              <p>Open this demo in a new tab without requiring a second login.</p>
            </div>

            <div className="sales-form-fields">
              <div className="sales-field sales-field-full">
                <label>Launch Mode</label>
                <input value="Platform-controlled launch in a new tab" readOnly />
              </div>

              <div className="sales-field sales-field-full">
                <label>Authentication</label>
                <input value="No separate demo login required" readOnly />
              </div>

              <div className="sales-field sales-field-full">
                <label>Launch URL</label>
                <input
                  value={demoAccount.full_domain ? `https://${demoAccount.full_domain}` : ""}
                  readOnly
                />
              </div>

              <div className="sales-field sales-field-full">
                <label>Created By</label>
                <input value={demoAccount.created_by_name || "—"} readOnly />
              </div>

              <div className="sales-field sales-field-full">
                <label>Created At</label>
                <input
                  value={demoAccount.created_at ? String(demoAccount.created_at).slice(0, 19).replace("T", " ") : ""}
                  readOnly
                />
              </div>
            </div>

            <div className="sales-header-actions" style={{ marginTop: "14px" }}>
              <button className="sales-primary-btn" onClick={handleOpenDemo} disabled={launching}>
                {launching ? "Opening..." : "Open Demo in New Tab"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DemoAccountProfile;